import { API, SHOP_URL } from './api'
import { DateField } from './DatePicker'
import { openPrint } from './StockAdmin'
import SeasonalOrderFormSettingsPanel from './SeasonalOrderFormSettingsPanel'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Vianočné (sezónne) objednávky – v administrácii pod Objednávkami.
 * Prehľad sezóny (či sa okno v e-shope zobrazuje a prečo nie), obsadenosť dní, tlač a jeden editor so všetkým nastavením.
 */

type Store = { id: string; name: string }
type Category = { id: string; name: string; parentId: string | null; visible?: boolean }
type StoreRule = { storeId: string; storeName: string; acceptsOrders: boolean; dailyOrderLimitOverride: number | null; unlimitedDailyOrders: boolean }
type Closure = { date: string; storeId: string | null; storeName?: string | null; reason: string | null }
type DayStat = { day: string; stores: Array<{ storeId: string; count: number; limit: number | null; closed: boolean }> }
type Rule = {
  id: string; name: string; type: 'CHRISTMAS' | 'EASTER' | 'OTHER'
  acceptOrdersFrom: string; acceptOrdersUntil: string; fulfillmentFrom: string; fulfillmentUntil: string
  requireRequestedFulfillmentAt: boolean; requireOnlinePayment?: boolean; defaultDailyOrderLimit: number | null; isActive: boolean
  stores: StoreRule[]; closures: Closure[]
  shopCategoryId?: string | null; pickupPaymentOnly?: boolean; shopTitle?: string | null; shopText?: string | null; orderCount?: number
  shopStatus?: { visible: boolean; reasons: string[]; productCount: number }
  stats?: { total: number; totalCents?: number; days: DayStat[] }
}
type Draft = {
  name: string; type: Rule['type']; acceptOrdersFrom: string; acceptOrdersUntil: string; fulfillmentFrom: string; fulfillmentUntil: string
  requireRequestedFulfillmentAt: boolean; requireOnlinePayment: boolean; defaultDailyOrderLimit: number | null
  stores: Array<Omit<StoreRule, 'storeName'>>; closures: Closure[]
  shopCategoryId: string | null; pickupPaymentOnly: boolean; shopTitle: string | null; shopText: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseSeasonalOrdersPayload(value: unknown): { rules: Rule[]; stores: Store[]; categories: Category[]; migrationRequired: boolean } {
  if (!isRecord(value) || !Array.isArray(value.rules) || !Array.isArray(value.stores)) {
    throw new Error('Server vrátil neúplnú odpoveď pre sezónne objednávky. Skúste načítanie zopakovať.')
  }
  const ids = new Set<string>()
  for (const rule of value.rules) {
    if (!isRecord(rule) || typeof rule.id !== 'string' || !rule.id.trim() || typeof rule.name !== 'string') throw new Error('Odpoveď servera obsahuje neplatnú sezónu. Skúste načítanie zopakovať.')
    if (ids.has(rule.id)) throw new Error('Odpoveď servera obsahuje duplicitnú sezónu. Skúste načítanie zopakovať.')
    ids.add(rule.id)
  }
  for (const store of value.stores) if (!isRecord(store) || typeof store.id !== 'string' || typeof store.name !== 'string') throw new Error('Odpoveď servera obsahuje neplatnú predajňu.')
  return {
    rules: value.rules as Rule[],
    stores: value.stores as Store[],
    categories: Array.isArray(value.categories) ? value.categories.filter((c): c is Category => isRecord(c) && typeof c.id === 'string' && typeof c.name === 'string') : [],
    migrationRequired: value.migrationRequired === true,
  }
}

const year = new Date().getFullYear()
function newDraft(stores: Store[], categories: Category[]): Draft {
  const xmas = categories.find(c => /viano/i.test(c.name))
  return {
    name: `Vianoce ${year}`, type: 'CHRISTMAS',
    acceptOrdersFrom: new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava' }).format(new Date()), acceptOrdersUntil: `${year}-12-20`,
    fulfillmentFrom: `${year}-12-22`, fulfillmentUntil: `${year}-12-23`,
    requireRequestedFulfillmentAt: true, requireOnlinePayment: false, defaultDailyOrderLimit: 30,
    stores: stores.map(store => ({ storeId: store.id, acceptsOrders: true, dailyOrderLimitOverride: null, unlimitedDailyOrders: false })), closures: [],
    shopCategoryId: xmas?.id ?? null, pickupPaymentOnly: true, shopTitle: null, shopText: null,
  }
}
function toDraft(rule: Rule, stores: Store[]): Draft {
  return {
    name: rule.name, type: rule.type, acceptOrdersFrom: rule.acceptOrdersFrom, acceptOrdersUntil: rule.acceptOrdersUntil,
    fulfillmentFrom: rule.fulfillmentFrom, fulfillmentUntil: rule.fulfillmentUntil,
    requireRequestedFulfillmentAt: rule.requireRequestedFulfillmentAt, requireOnlinePayment: Boolean(rule.requireOnlinePayment), defaultDailyOrderLimit: rule.defaultDailyOrderLimit,
    // Predajne, ktoré pribudli po vytvorení sezóny, sa doplnia (zapnutie vyžaduje všetky aktívne predajne).
    stores: [...rule.stores.filter(s => stores.some(x => x.id === s.storeId)).map(({ storeId, acceptsOrders, dailyOrderLimitOverride, unlimitedDailyOrders }) => ({ storeId, acceptsOrders, dailyOrderLimitOverride, unlimitedDailyOrders })),
      ...stores.filter(s => !rule.stores.some(x => x.storeId === s.id)).map(s => ({ storeId: s.id, acceptsOrders: true, dailyOrderLimitOverride: null, unlimitedDailyOrders: false }))],
    closures: rule.closures.filter(c => c.storeId === null || stores.some(s => s.id === c.storeId)).map(({ date, storeId, reason }) => ({ date, storeId, reason })),
    shopCategoryId: rule.shopCategoryId ?? null, pickupPaymentOnly: rule.pickupPaymentOnly !== false, shopTitle: rule.shopTitle ?? null, shopText: rule.shopText ?? null,
  }
}

const d = (day: string) => { const [, m, dd] = day.split('-').map(Number); return `${dd}. ${m}.` }
const dLong = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('sk-SK', { weekday: 'short', day: 'numeric', month: 'numeric' })
const range = (a: string, b: string) => a === b ? d(a) : `${d(a)} – ${d(b)}`
const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const seasonIcon = (type: Rule['type']) => type === 'CHRISTMAS' ? '🎄' : type === 'EASTER' ? '🐣' : '✨'
function categoryOptions(categories: Category[]) {
  const out: Array<{ id: string; label: string }> = []
  const walk = (parentId: string | null, depth: number) => categories.filter(c => c.parentId === parentId).forEach(c => { out.push({ id: c.id, label: `${'— '.repeat(depth)}${c.name}${c.visible === false ? ' (skrytá)' : ''}` }); walk(c.id, depth + 1) })
  walk(null, 0)
  return out
}

export default function SeasonalOrdersSettings({ onShowOrders }: { onShowOrders?: () => void }) {
  const [stores, setStores] = useState<Store[]>([])
  const [rules, setRules] = useState<Rule[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const deletionInProgress = useRef(false)
  const [error, setError] = useState('')
  const [editorError, setEditorError] = useState('')
  const [notice, setNotice] = useState('')
  const [migrationRequired, setMigrationRequired] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const response = await fetch(`${API}/api/admin/seasonal-orders`, { credentials: 'include' })
      const data = await response.json().catch(() => null)
      if (response.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
      if (!response.ok) throw new Error((isRecord(data) && typeof data.error === 'string' ? data.error : null) || `Sezóny sa nepodarilo načítať (HTTP ${response.status}).`)
      const payload = parseSeasonalOrdersPayload(data)
      setRules(payload.rules); setStores(payload.stores); setCategories(payload.categories); setMigrationRequired(payload.migrationRequired)
    } catch (reason) { setRules([]); setError(reason instanceof Error ? reason.message : 'Sezóny sa nepodarilo načítať.') }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { void load() }, [load])

  const send = async (path: string, init: RequestInit) => {
    const response = await fetch(`${API}${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...init })
    const data = await response.json().catch(() => ({}))
    if (response.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
    if (!response.ok) throw new Error(data.error || `Akcia sa nepodarila (HTTP ${response.status}).`)
    return data
  }

  async function save() {
    if (!draft) return
    setSaving(true); setEditorError(''); setNotice('')
    try {
      await send(`/api/admin/seasonal-orders${editingId ? `/${encodeURIComponent(editingId)}` : ''}`, { method: editingId ? 'PUT' : 'POST', body: JSON.stringify(draft) })
      setNotice(editingId ? 'Zmeny sú uložené.' : 'Sezóna je vytvorená. Zapnite ju prepínačom, keď má začať platiť.')
      setDraft(null); setEditingId(null)
      await load()
    } catch (reason) { setEditorError(reason instanceof Error ? reason.message : 'Uloženie sa nepodarilo.') }
    finally { setSaving(false) }
  }

  async function toggle(rule: Rule) {
    const on = !rule.isActive
    if (!window.confirm(on ? `Zapnúť sezónu „${rule.name}“? Zákazníci uvidia v e-shope okno so sezónnou ponukou (v období prijímania).` : `Vypnúť sezónu „${rule.name}“? Okno v e-shope zmizne; prijaté objednávky zostanú.`)) return
    setBusyId(rule.id); setError(''); setNotice('')
    try { await send(`/api/admin/seasonal-orders/${encodeURIComponent(rule.id)}/activation`, { method: 'PATCH', body: JSON.stringify({ isActive: on }) }); await load() }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Zmena sa nepodarila.') }
    finally { setBusyId(null) }
  }

  async function deleteRule(rule: Rule) {
    if (deletionInProgress.current || migrationRequired) return
    if (!window.confirm(`Naozaj chcete vymazať sezónu ${rule.name} (ID: ${rule.id})? Túto akciu nie je možné vrátiť späť.`)) return
    deletionInProgress.current = true; setDeletingId(rule.id); setError(''); setNotice('')
    try {
      const data = await send(`/api/admin/seasonal-orders/${encodeURIComponent(rule.id)}`, { method: 'DELETE' })
      if (data.deleted !== true || data.id !== rule.id) throw new Error('Server nepotvrdil vymazanie vybratej sezóny.')
      setNotice(`Sezóna ${rule.name} bola vymazaná.`); setDraft(null); setEditingId(null)
      await load()
    } catch (reason) { setEditorError(reason instanceof Error ? reason.message : 'Sezónu sa nepodarilo vymazať.') }
    finally { deletionInProgress.current = false; setDeletingId(null) }
  }

  const storeName = (id: string) => stores.find(s => s.id === id)?.name ?? rules.flatMap(r => r.stores).find(s => s.storeId === id)?.storeName ?? 'Predajňa'
  const open = (rule: Rule | null) => { setEditorError(''); setNotice(''); setEditingId(rule?.id ?? null); setDraft(rule ? toDraft(rule, stores) : newDraft(stores, categories)) }
  const editing = editingId ? rules.find(r => r.id === editingId) ?? null : null

  return <div className="so">
    <div className="so-top">
      <div><h2>🎄 Vianočné a sezónne objednávky</h2><p>Samostatné okno v e-shope so sezónnou ponukou, vlastnými dňami vyzdvihnutia a platbou pri prevzatí. Bežné objednávky to neovplyvňuje.</p></div>
      <div className="so-top-actions">
        <button type="button" className="pa-ghost" onClick={() => void load()} disabled={loading}>{loading ? 'Načítavam…' : error ? 'Opakovať načítanie' : '↻ Obnoviť'}</button>
        {onShowOrders && <button type="button" className="pa-ghost" onClick={onShowOrders}>Zoznam vianočných objednávok</button>}
        <button type="button" className="primary-btn" disabled={loading || migrationRequired} onClick={() => open(null)}>＋ Nová sezóna</button>
      </div>
    </div>
    <SeasonalOrderFormSettingsPanel />
    {migrationRequired && <p className="error-message" role="status">Databáza ešte nie je pripravená na sezónne objednávky – nasaďte aktualizáciu servera.</p>}
    {error && <p className="error-message" role="alert">{error}</p>}
    {notice && <p className="success-message" role="status">{notice}</p>}
    {loading && !rules.length && <p className="empty-message">Načítavam sezóny…</p>}

    {!loading && !error && !rules.length && <div className="so-empty">
      <span aria-hidden="true">🎄</span>
      <strong>Zatiaľ nemáte žiadnu sezónu</strong>
      <p>Pripravte Vianoce: vyberte kategóriu s vianočnou ponukou, dni vyzdvihnutia a limity predajní.</p>
      <button type="button" className="primary-btn" onClick={() => open(null)}>Pripraviť Vianoce {year}</button>
    </div>}

    {rules.map(rule => {
      const status = rule.shopStatus
      const days = rule.stats?.days ?? []
      return <article key={rule.id} className={`so-season type-${rule.type.toLowerCase()} ${rule.isActive ? 'is-on' : ''}`}>
        <header className="so-season-head">
          <div className="so-season-title">
            <span className="so-season-icon" aria-hidden="true">{seasonIcon(rule.type)}</span>
            <div>
              <h3>{rule.name}</h3>
              <div className="so-chips">
                <span>Prijímanie <b>{range(rule.acceptOrdersFrom, rule.acceptOrdersUntil)}</b></span>
                <span>Vyzdvihnutie <b>{range(rule.fulfillmentFrom, rule.fulfillmentUntil)}</b></span>
                <span>{rule.pickupPaymentOnly !== false ? 'Platba pri prevzatí' : rule.requireOnlinePayment ? 'Platba vopred online' : 'Platba pri prevzatí alebo online'}</span>
              </div>
            </div>
          </div>
          <div className="so-season-actions">
            <button type="button" className="pa-ghost" title="Tlačová objednávka pre zákazníkov na vyplnenie v predajni (A4/A5)" onClick={() => openPrint(`/api/admin/seasonal-orders/${encodeURIComponent(rule.id)}/order-form-print`)}>🖨 Tlačová objednávka</button>
            <button type="button" role="switch" aria-checked={rule.isActive} className={`mt-switch so-switch ${rule.isActive ? 'is-on' : ''}`} disabled={busyId === rule.id || migrationRequired} onClick={() => void toggle(rule)}>
              <span className="mt-switch-track"><span className="mt-switch-knob" /></span><b>{rule.isActive ? 'Zapnutá' : 'Vypnutá'}</b>
            </button>
            <button type="button" className="pa-ghost" onClick={() => open(rule)}>✎ Upraviť</button>
          </div>
        </header>

        {status && (status.visible
          ? <div className="so-status is-ok"><span aria-hidden="true">✓</span><div><strong>Okno „{rule.shopTitle || (rule.type === 'CHRISTMAS' ? 'Vianočná objednávka' : rule.name)}“ sa v e-shope zobrazuje</strong><small>{status.productCount} {status.productCount === 1 ? 'výrobok' : status.productCount < 5 ? 'výrobky' : 'výrobkov'} v ponuke · zákazníci objednávajú do {d(rule.acceptOrdersUntil)}</small></div><a className="pa-ghost" href={`${SHOP_URL}/#vianocna-objednavka`} target="_blank" rel="noopener">Otvoriť v e-shope ↗</a></div>
          : <div className="so-status is-warn"><span aria-hidden="true">!</span><div><strong>Okno sa v e-shope zatiaľ nezobrazuje</strong><ul>{status.reasons.map(r => <li key={r}>{r}</li>)}</ul></div><button type="button" className="pa-ghost" onClick={() => open(rule)}>Opraviť nastavenie</button></div>)}

        <div className="so-kpis">
          <div><small>Objednávky</small><strong>{rule.stats?.total ?? rule.orderCount ?? 0}</strong></div>
          <div><small>Spolu</small><strong>{euro(rule.stats?.totalCents ?? 0)}</strong></div>
          <div><small>Kategória s ponukou</small><strong>{categories.find(c => c.id === rule.shopCategoryId)?.name ?? '—'}</strong></div>
          <div><small>Limit na deň a predajňu</small><strong>{rule.defaultDailyOrderLimit ?? 'bez limitu'}</strong></div>
        </div>

        {days.length > 0 && <div className="so-days">
          {days.map(day => <div key={day.day} className="so-day">
            <div className="so-day-head"><strong>{dLong(day.day)}</strong>
              <span>
                <button type="button" className="so-print" title="Tlač vianočných objednávok na tento deň (A5)" onClick={() => openPrint(`/api/admin/orders-print?${new URLSearchParams({ day: day.day, seasonal: '1', format: 'a5' })}`)}>🖨 A5</button>
                <button type="button" className="so-print" title="Tlač vianočných objednávok na tento deň (A4)" onClick={() => openPrint(`/api/admin/orders-print?${new URLSearchParams({ day: day.day, seasonal: '1', format: 'a4' })}`)}>A4</button>
              </span>
            </div>
            {day.stores.map(s => { const pct = s.limit ? Math.min(100, Math.round(s.count / s.limit * 100)) : 0; return <div key={s.storeId} className={`so-cap ${s.closed ? 'is-closed' : s.limit && s.count >= s.limit ? 'is-full' : ''}`}>
              <span>{storeName(s.storeId)}</span>
              <b>{s.closed ? 'zatvorené' : `${s.count}${s.limit ? ` / ${s.limit}` : ''}`}</b>
              {!s.closed && s.limit ? <i style={{ width: `${pct}%` }} /> : null}
            </div> })}
          </div>)}
        </div>}
        <small className="seasonal-rule-id">ID: {rule.id}</small>
      </article>
    })}

    {draft && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget && !saving) { setDraft(null); setEditingId(null) } }}>
      <section className="so-editor" role="dialog" aria-modal="true" aria-labelledby="so-editor-title">
        <header><div><p>{editingId ? 'Úprava sezóny' : 'Nová sezóna'}</p><h2 id="so-editor-title">{draft.name || 'Sezóna'}</h2></div><button type="button" className="close-btn" aria-label="Zavrieť" onClick={() => { setDraft(null); setEditingId(null) }}>×</button></header>
        <div className="so-editor-body">
          <section>
            <h4>Základ</h4>
            <div className="so-grid">
              <label>Názov<input value={draft.name} maxLength={120} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
              <label>Druh<select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value as Draft['type'] })}><option value="CHRISTMAS">Vianoce</option><option value="EASTER">Veľká noc</option><option value="OTHER">Iné</option></select></label>
            </div>
          </section>
          <section>
            <h4>Obdobie</h4>
            <div className="so-grid four">
              <DateField label="Prijímanie od" required value={draft.acceptOrdersFrom} onChange={v => setDraft({ ...draft, acceptOrdersFrom: v })} max={draft.acceptOrdersUntil || undefined} />
              <DateField label="Prijímanie do" required value={draft.acceptOrdersUntil} onChange={v => setDraft({ ...draft, acceptOrdersUntil: v })} min={draft.acceptOrdersFrom || undefined} />
              <DateField label="Vyzdvihnutie od" required value={draft.fulfillmentFrom} onChange={v => setDraft({ ...draft, fulfillmentFrom: v })} max={draft.fulfillmentUntil || undefined} />
              <DateField label="Vyzdvihnutie do" required value={draft.fulfillmentUntil} onChange={v => setDraft({ ...draft, fulfillmentUntil: v })} min={draft.fulfillmentFrom || undefined} />
            </div>
            <p className="so-hint">Okno sa v e-shope ukáže od dňa „Prijímanie od“ (ak je sezóna zapnutá). Zákazník si vyberie jeden z dní vyzdvihnutia.</p>
          </section>
          <section>
            <h4>Ponuka v e-shope</h4>
            <div className="so-grid">
              <label>Kategória s ponukou *<select value={draft.shopCategoryId ?? ''} onChange={e => setDraft({ ...draft, shopCategoryId: e.target.value || null })}><option value="">— vyberte kategóriu —</option>{categoryOptions(categories).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select><small>Výrobky z kategórie (aj podkategórií). V bežnom e-shope zostanú tiež.</small></label>
              <label>Nadpis okna<input value={draft.shopTitle ?? ''} maxLength={80} placeholder={draft.type === 'CHRISTMAS' ? 'Vianočná objednávka' : draft.type === 'EASTER' ? 'Veľkonočná objednávka' : draft.name} onChange={e => setDraft({ ...draft, shopTitle: e.target.value || null })} /></label>
              <label className="wide">Text pre zákazníkov<textarea rows={2} maxLength={600} value={draft.shopText ?? ''} placeholder={`Napr. Vianočné objednávky prijímame do ${d(draft.acceptOrdersUntil || `${year}-12-20`)}. Vyzdvihnutie ${range(draft.fulfillmentFrom || `${year}-12-22`, draft.fulfillmentUntil || `${year}-12-23`)} v predajni.`} onChange={e => setDraft({ ...draft, shopText: e.target.value || null })} /></label>
              <label className="so-check wide"><input type="checkbox" checked={draft.pickupPaymentOnly} onChange={e => setDraft({ ...draft, pickupPaymentOnly: e.target.checked })} /> Platba iba pri prevzatí v predajni (osobný odber)</label>
              {!draft.pickupPaymentOnly && <label className="so-check wide"><input type="checkbox" checked={draft.requireOnlinePayment} onChange={e => setDraft({ ...draft, requireOnlinePayment: e.target.checked })} /> Iba platba vopred kartou online</label>}
            </div>
          </section>
          <section>
            <h4>Predajne a kapacita</h4>
            <label className="so-limit">Limit objednávok na predajňu a deň<input type="number" min="1" max="100000" value={draft.defaultDailyOrderLimit ?? ''} placeholder="bez limitu" onChange={e => setDraft({ ...draft, defaultDailyOrderLimit: e.target.value ? Number(e.target.value) : null })} /></label>
            <div className="so-stores">{draft.stores.map((sr, i) => {
              const set = (patch: Partial<typeof sr>) => setDraft({ ...draft, stores: draft.stores.map((x, j) => j === i ? { ...x, ...patch } : x) })
              return <div key={sr.storeId} className={`so-store ${sr.acceptsOrders ? '' : 'is-off'}`}>
                <label className="so-check"><input type="checkbox" checked={sr.acceptsOrders} onChange={e => set({ acceptsOrders: e.target.checked })} /> <b>{storeName(sr.storeId)}</b></label>
                <input type="number" min="1" max="100000" aria-label={`Vlastný limit – ${storeName(sr.storeId)}`} disabled={!sr.acceptsOrders || sr.unlimitedDailyOrders} value={sr.dailyOrderLimitOverride ?? ''} placeholder={`limit ${draft.defaultDailyOrderLimit ?? '–'}`} onChange={e => set({ dailyOrderLimitOverride: e.target.value ? Number(e.target.value) : null })} />
                <label className="so-check"><input type="checkbox" disabled={!sr.acceptsOrders} checked={sr.unlimitedDailyOrders} onChange={e => set({ unlimitedDailyOrders: e.target.checked, dailyOrderLimitOverride: null })} /> bez limitu</label>
              </div>
            })}</div>
          </section>
          <ClosuresEditor draft={draft} stores={stores} storeName={storeName} onChange={closures => setDraft({ ...draft, closures })} />
          {editorError && <p className="error-message" role="alert">{editorError}</p>}
        </div>
        <footer>
          {editing && <button type="button" className="seasonal-delete-btn" aria-label={`Vymazať sezónu ${editing.name} (ID: ${editing.id})`} title={editing.isActive ? 'Pred vymazaním sezónu vypnite.' : 'Vymazať sezónu'} disabled={editing.isActive || deletingId !== null || migrationRequired} onClick={() => void deleteRule(editing)}>{deletingId === editing.id ? 'Vymazávam…' : 'Vymazať sezónu'}</button>}
          <span />
          <button type="button" className="pa-ghost" onClick={() => { setDraft(null); setEditingId(null) }}>Zrušiť</button>
          <button type="button" className="primary-btn" disabled={saving || !stores.length || migrationRequired} onClick={() => void save()}>{saving ? 'Ukladám…' : 'Uložiť'}</button>
        </footer>
      </section>
    </div>}
  </div>
}

function ClosuresEditor({ draft, stores, storeName, onChange }: { draft: Draft; stores: Store[]; storeName: (id: string) => string; onChange: (closures: Closure[]) => void }) {
  const [date, setDate] = useState('')
  const [storeId, setStoreId] = useState('*')
  const [reason, setReason] = useState('')
  const add = () => {
    if (!date) return
    const sid = storeId === '*' ? null : storeId
    if (draft.closures.some(c => c.date === date && c.storeId === sid)) return
    onChange([...draft.closures, { date, storeId: sid, reason: reason.trim() || null }]); setDate(''); setReason('')
  }
  return <section>
    <h4>Zatvorené dni <small>– nepovinné</small></h4>
    <div className="so-closure-add">
      <DateField value={date} onChange={setDate} placeholder="Deň" min={draft.fulfillmentFrom || undefined} max={draft.fulfillmentUntil || undefined} />
      <select aria-label="Predajňa" value={storeId} onChange={e => setStoreId(e.target.value)}><option value="*">Všetky predajne</option>{stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
      <input aria-label="Poznámka" value={reason} maxLength={300} placeholder="Poznámka" onChange={e => setReason(e.target.value)} />
      <button type="button" className="pa-ghost" disabled={!date} onClick={add}>Pridať</button>
    </div>
    {draft.closures.length > 0 && <ul className="so-closures">{draft.closures.map((c, i) => <li key={`${c.date}-${c.storeId ?? '*'}`}>{d(c.date)} · {c.storeId ? storeName(c.storeId) : 'všetky predajne'}{c.reason ? ` · ${c.reason}` : ''}<button type="button" aria-label="Odstrániť" onClick={() => onChange(draft.closures.filter((_, j) => j !== i))}>×</button></li>)}</ul>}
  </section>
}
